/** Cronologia "chi ha fatto cosa" su una pratica, condivisa fra i verticali.
 *
 * Fonte unica `audit_log`, la stessa della pagina globale "Registro attività".
 * Il pannello non sa da quale flusso arriva: riceve un `fetcher`, così ogni
 * verticale usa il proprio endpoint (`/audit/by-request/{id}` per RDI e DDT,
 * `/bsc/{id}/activity` per BSC) senza che questo componente cresca di rami.
 *
 * Due scelte che lo distinguono dalla versione da cui nasce (il pannello locale
 * del dettaglio BSC):
 *
 * 1. **I dettagli vengono letti, non riversati.** Lì `details` finiva in un
 *    `<pre>` con il JSON: accettabile quando l'unica domanda è "chi ha toccato la
 *    pratica", inutile quando la domanda è "cosa ha cambiato" — che per una RDI è
 *    la domanda principale. Le forme note (`corrections`, `motivo_original` →
 *    `motivo_corrected`, `forced_entities`, `categories`, `previous_status`)
 *    vengono rese come righe leggibili; il JSON grezzo resta raggiungibile sotto
 *    "dati completi", perché una forma non prevista non deve sparire.
 * 2. **Il prima → dopo mostra ciò che è stato salvato, senza abbellirlo.** Nelle
 *    correzioni RDI l'etichetta del valore precedente coincide con il codice
 *    (`original_label` vale `"C1609"`): si mostra il codice, non un nome inventato
 *    o un trattino. Se un giorno si vorranno i nomi, va risolto all'origine.
 */

import { useEffect, useState } from "react";

import type { AuditEntry } from "../api/types";
import {
  actionMeta,
  correctedFieldLabel,
  correctionCategoryLabel,
  rejectCategoryLabel,
} from "./activityLabels";

/** Testo lungo (il motivo di un ticket) troncato per stare su una riga. */
const TESTO_MAX = 160;

function taglia(valore: unknown, max = TESTO_MAX): string {
  const s = valore === null || valore === undefined || valore === "" ? "—" : String(valore);
  return s.length <= max ? s : `${s.slice(0, max - 1).trimEnd()}…`;
}

export function formatActivityDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString("it-IT", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

/** Una modifica, nella forma in cui va letta: cosa, da cosa, a cosa. */
export interface Cambiamento {
  campo: string;
  prima?: string;
  dopo?: string;
  categorie?: string[];
  nota?: string;
}

/** Estrae le modifiche dalle forme che `details` assume davvero.
 *
 * Esportata perché la usa anche il riassunto d'esito nel dettaglio RDI: se il
 * riquadro in cima e la cronologia sotto leggessero `details` in due modi
 * diversi, mostrerebbero due versioni della stessa modifica.
 */
export function estraiCambiamenti(entry: AuditEntry): Cambiamento[] {
  const d = entry.details ?? {};
  const out: Cambiamento[] = [];

  // `correction`: elenco esplicito, la forma più ricca.
  const corr = d.corrections;
  if (Array.isArray(corr)) {
    for (const c of corr as Array<Record<string, unknown>>) {
      out.push({
        campo: correctedFieldLabel(String(c.field_corrected ?? "")),
        prima: taglia(c.original_label ?? c.original_pk),
        dopo: taglia(c.corrected_label ?? c.corrected_pk),
        categorie: Array.isArray(c.categories) ? (c.categories as string[]) : undefined,
        nota: c.note ? String(c.note) : undefined,
      });
    }
  }

  // `approve`: il motivo riscritto a mano. È la modifica più frequente e non
  // compare in `processing_corrections`, che conosce solo cliente/edificio/impianto.
  if (d.motivo_original !== undefined || d.motivo_corrected !== undefined) {
    out.push({
      campo: "Motivo",
      prima: taglia(d.motivo_original),
      dopo: taglia(d.motivo_corrected),
    });
  }

  // `approve` con force_matches: si sa *quali* entità l'operatore ha imposto, non
  // i valori. Una voce **per entità** e non una sola voce "Imposti a mano" con
  // l'elenco dentro: il riassunto d'esito elenca i campi toccati, e con la forma
  // aggregata leggeva "1 modifica: Imposti a mano" — che non dice quale campo sia
  // cambiato. Visto guardando la pagina il 2026-08-04.
  const forced = d.forced_entities;
  if (Array.isArray(forced)) {
    for (const k of forced as string[]) {
      out.push({ campo: correctedFieldLabel(k), dopo: "imposto a mano dall'operatore" });
    }
  }

  // `reopen`: da quale stato si è tornati indietro.
  if (d.previous_status) {
    out.push({ campo: "Stato precedente", dopo: String(d.previous_status) });
  }

  return out;
}

function RigaCambiamento({ c }: { c: Cambiamento }) {
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: "var(--space-1)", alignItems: "baseline" }}>
      <span className="text-muted fw-medium">{c.campo}:</span>
      {c.prima !== undefined && (
        <>
          <span style={{ textDecoration: "line-through", color: "var(--indaco-gray-600)" }}>{c.prima}</span>
          <span className="material-symbols-outlined icon-sm" style={{ color: "var(--indaco-gray-400)" }} aria-hidden>
            arrow_forward
          </span>
        </>
      )}
      {c.dopo !== undefined && <span className="fw-medium">{c.dopo}</span>}
      {c.categorie?.map((k) => (
        <span key={k} className="badge badge-neutral badge-sm">
          {correctionCategoryLabel(k)}
        </span>
      ))}
      {c.nota && <span className="text-muted text-sm">— {c.nota}</span>}
    </div>
  );
}

/** Carica la cronologia di una pratica.
 *
 * Separato dal pannello perché nella stessa pagina la leggono in due: il riassunto
 * d'esito in cima e la cronologia in fondo. Se ognuno facesse la propria richiesta
 * sarebbero due chiamate e, nel momento fra le due risposte, due versioni della
 * stessa pratica.
 */
export function useActivity(
  fetcher: () => Promise<AuditEntry[]>,
  version?: string,
): { entries: AuditEntry[]; loading: boolean } {
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    fetcher()
      .then((r) => {
        if (alive) setEntries(r);
      })
      .catch(() => {
        if (alive) setEntries([]);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
    // `fetcher` è ricreata a ogni render dal chiamante: dipendere da lei
    // ricaricherebbe in ciclo. La dipendenza reale è `version`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [version]);

  return { entries, loading };
}

export function ActivityPanel({
  entries,
  loading,
  titolo = "Cronologia",
  vuoto = "Nessuna attività registrata su questa pratica.",
}: {
  entries: AuditEntry[];
  loading: boolean;
  titolo?: string;
  vuoto?: string;
}) {
  const [openId, setOpenId] = useState<string | null>(null);

  return (
    <div className="card" style={{ overflow: "hidden" }}>
      <div className="card-header-accent card-header-accent-neutral">
        <span className="material-symbols-outlined" style={{ fontSize: 20, color: "var(--indaco-gray-600)" }}>
          history
        </span>
        <span style={{ fontWeight: "var(--font-weight-semibold)", color: "var(--indaco-gray-700)" }}>{titolo}</span>
        <span className="text-muted text-sm" style={{ marginLeft: "auto" }}>chi ha fatto cosa</span>
      </div>
      {loading ? (
        <div style={{ padding: "var(--space-4)", textAlign: "center" }}>
          <span className="spinner" />
        </div>
      ) : entries.length === 0 ? (
        <div className="text-muted text-sm" style={{ padding: "var(--space-4)" }}>{vuoto}</div>
      ) : (
        <div style={{ padding: "var(--space-2) 0" }}>
          {entries.map((e) => {
            const meta = actionMeta(e.action);
            const cambiamenti = estraiCambiamenti(e);
            const categorie = e.feedback_categories ?? [];
            const isOpen = openId === e.id;
            // La nota e le categorie di uno scarto sono la risposta alla domanda
            // "perché", quindi restano sempre visibili: non sono un dettaglio da
            // aprire. Sotto il chevron va ciò che serve solo a chi indaga.
            const approfondibile = cambiamenti.length > 0 || !!e.lesson_learned || !!e.details;
            return (
              <div
                key={e.id}
                style={{ padding: "var(--space-2) var(--space-4)", borderBottom: "1px solid var(--indaco-gray-100)" }}
              >
                <div
                  role={approfondibile ? "button" : undefined}
                  tabIndex={approfondibile ? 0 : undefined}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "var(--space-2)",
                    cursor: approfondibile ? "pointer" : "default",
                  }}
                  onClick={() => approfondibile && setOpenId(isOpen ? null : e.id)}
                  onKeyDown={(ev) => {
                    if (approfondibile && (ev.key === "Enter" || ev.key === " ")) {
                      ev.preventDefault();
                      setOpenId(isOpen ? null : e.id);
                    }
                  }}
                >
                  <span className="material-symbols-outlined" style={{ fontSize: 18, color: meta.color }}>
                    {meta.icon}
                  </span>
                  <span style={{ fontWeight: "var(--font-weight-medium)", color: meta.color }}>{meta.label}</span>
                  <span className="text-muted text-sm truncate">
                    ·&nbsp;{e.operator_name || e.operator_id || "—"}
                  </span>
                  <span className="text-muted text-sm" style={{ marginLeft: "auto", whiteSpace: "nowrap" }}>
                    {formatActivityDate(e.created_at)}
                  </span>
                  {approfondibile && (
                    <span className="material-symbols-outlined icon-sm" style={{ color: "var(--indaco-gray-400)" }}>
                      {isOpen ? "expand_less" : "expand_more"}
                    </span>
                  )}
                </div>

                {/* Sempre visibile: il perché. */}
                {(categorie.length > 0 || e.feedback_note) && (
                  <div style={{ marginTop: "var(--space-1)", paddingLeft: 26, fontSize: "var(--font-size-sm)" }}>
                    {categorie.length > 0 && (
                      <div style={{ display: "flex", gap: "var(--space-1)", flexWrap: "wrap", marginBottom: 2 }}>
                        {/* Il vocabolario dipende dall'azione: `correction` usa le
                            categorie di correzione, lo scarto quelle di rifiuto.
                            Applicando quello sbagliato la categoria cadeva nel
                            fallback e si leggeva "match ambiguo" invece di
                            "Abbinamento ambiguo" — visto sulla pagina. */}
                        {categorie.map((k) => (
                          <span key={k} className="badge badge-neutral badge-sm">
                            {e.action.includes("correction") ? correctionCategoryLabel(k) : rejectCategoryLabel(k)}
                          </span>
                        ))}
                      </div>
                    )}
                    {e.feedback_note && <div>{e.feedback_note}</div>}
                  </div>
                )}

                {isOpen && approfondibile && (
                  <div
                    style={{
                      marginTop: "var(--space-2)",
                      paddingLeft: 26,
                      fontSize: "var(--font-size-sm)",
                      display: "grid",
                      gap: "var(--space-1)",
                    }}
                  >
                    {cambiamenti.map((c, i) => (
                      <RigaCambiamento key={`${c.campo}-${i}`} c={c} />
                    ))}
                    {e.lesson_learned && (
                      <div style={{ color: "var(--indaco-success)" }}>
                        <span className="text-muted fw-medium">Apprendimento: </span>
                        {e.lesson_learned}
                      </div>
                    )}
                    {e.details && (
                      <details>
                        <summary className="text-muted text-xs" style={{ cursor: "pointer" }}>
                          dati completi
                        </summary>
                        <pre
                          style={{
                            fontSize: "var(--font-size-xs)",
                            whiteSpace: "pre-wrap",
                            margin: "var(--space-1) 0 0",
                            color: "var(--indaco-gray-700)",
                          }}
                        >
                          {JSON.stringify(e.details, null, 2)}
                        </pre>
                      </details>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
